import { BACKEND_URL } from '../config';
import { APIEndpoint, RequestInput } from '../types';

export enum HTTPMethods {
  DELETE = 'DELETE',
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
}

export enum APIRequests {
  AdminConfig = 'adminConfig',
  AdminCheckOrderStatus = 'adminCheckOrderStatus',
  UserBloodTestDataList = 'userBloodTestDataList',
  UserBloodTestDataUpdate = 'userBloodTestDataUpdate',
  Login = 'login',
  ProductList = 'productList',
  QuestionList = 'questionList',
  QuizGetAdmin = 'quizGetAdmin',
  QuizUpdate = 'quizUpdate',
  QuizList = 'quizList',
  UserCardGet = 'userCardGet',
  UserCardUpdate = 'userCardUpdate',
  UsersGet = 'usersGet',
  UsersList = 'usersList',
  UsersListSubscriptions = 'usersListSubscriptions',
  UsersListOrders = 'usersListOrders',
  UserMe = 'userMe',
  UserNutrientsList = 'userNutrientsList',
  UserRequest = 'userRequest',
  UserShipmentsList = 'usershipmentslist',
  UserSubscriptionsList = 'userSubscriptionsList',
  UserSubscriptionsManage = 'userSubscriptionsManage',
  UserUpdate = 'userUpdate',
  Verify = 'verify',
  UserUpdateProfile = 'userUpdateProfile',
}

const apiEndpoints: Record<APIRequests, APIEndpoint> = {
  adminConfig: {
    path: '/admin/config',
    method: HTTPMethods.GET,
  },
  adminCheckOrderStatus: {
    path: '/admin/execute/check-order-processing-status',
    method: HTTPMethods.POST,
  },
  userBloodTestDataList: {
    path: '/analytes/blood-test-data',
    method: HTTPMethods.GET,
  },
  userBloodTestDataUpdate: {
    path: '/analytes/blood-test-data',
    method: HTTPMethods.POST,
  },
  login: {
    path: '/auth/token',
    method: HTTPMethods.POST,
  },
  productList: {
    path: '/products',
    method: HTTPMethods.GET,
  },
  questionList: {
    path: '/quizzes/questions',
    method: HTTPMethods.GET,
    queryParams: ['q', 'sort', 'sortDir', 'page'],
  },
  quizGetAdmin: {
    path: '/quizzes/admin/:id',
    method: HTTPMethods.GET,
    pathParams: ['id'],
  },
  quizList: {
    path: '/quizzes',
    method: HTTPMethods.GET,
    queryParams: ['q', 'sort', 'sortDir', 'page'],
  },
  quizUpdate: {
    path: '/quizzes',
    method: HTTPMethods.PUT,
  },
  userCardGet: {
    path: '/users/:id/card',
    method: HTTPMethods.GET,
    pathParams: ['id'],
  },
  userCardUpdate: {
    path: '/users/:id/card',
    method: HTTPMethods.PUT,
    pathParams: ['id'],
  },
  usersList: {
    path: '/users',
    method: HTTPMethods.GET,
    queryParams: ['q', 'sort', 'sortDir', 'page'],
  },
  usersListSubscriptions: {
    path: '/users/subscriptions',
    method: HTTPMethods.GET,
    queryParams: ['q', 'sort', 'sortDir', 'page'],
  },
  usersListOrders: {
    path: '/users/orders',
    method: HTTPMethods.GET,
    queryParams: ['q', 'sort', 'sortDir', 'page'],
  },
  usersGet: {
    path: '/users/:id',
    method: HTTPMethods.GET,
    pathParams: ['id'],
  },
  userNutrientsList: {
    path: '/users/nutrients',
    method: HTTPMethods.GET,
  },
  userMe: {
    path: '/users/me',
    method: HTTPMethods.GET,
  },
  userRequest: {
    path: '/users/:id/request',
    method: HTTPMethods.POST,
    pathParams: ['id'],
  },
  usershipmentslist: {
    path: '/shipments',
    method: HTTPMethods.GET,
  },
  userSubscriptionsList: {
    path: '/subscriptions',
    method: HTTPMethods.GET,
  },
  userSubscriptionsManage: {
    path: '/subscriptions',
    method: HTTPMethods.PUT,
  },
  userUpdate: {
    path: '/users',
    method: HTTPMethods.PUT,
  },
  verify: {
    path: '/auth/verify',
    method: HTTPMethods.POST,
  },
  userUpdateProfile: {
    path: '/users/me',
    method: HTTPMethods.PUT,
  },
};

export async function sendRequest<T>(
  endpoint: APIRequests,
  input?: RequestInput
): Promise<T> {
  const req = MakeRequest(endpoint, input || {});
  const reqParams = apiEndpoints[endpoint];
  return fetch(req, {
    credentials: 'include',
  })
    .then(async (res) => {
      if (!res.ok) {
        if (res.status === 401 && endpoint === APIRequests.UserMe) {
          localStorage.clear();
        }
        const err = await res.json();
        throw err;
      }
      return res;
    })
    .then((res) => {
      return res
        .json()
        .then((json) => (reqParams.typeOut ? reqParams.typeOut(json) : json))
        .catch(() => {
          return {};
        });
    })
    .catch((error: Error) => {
      console.error('API Error:', error);
      throw error;
    });
}

// eslint-disable-next-line complexity
export const MakeRequest = (
  req: APIRequests,
  input: RequestInput
): RequestInfo => {
  const { method, path, pathParams, queryParams, typeIn } = apiEndpoints[req];

  let fixedPath = BACKEND_URL + path;
  if (pathParams?.length && input.pathParams) {
    for (const p of pathParams) {
      fixedPath = fixedPath.replace(`/:${p}`, `/${input.pathParams[p]}`);
    }
  }

  if (queryParams?.length && input.queryParams) {
    const params = Object.keys(input.queryParams)
      .filter((k) => queryParams.indexOf(k) >= 0)
      .map((k) => [k, input.queryParams?.[k] || '']);
    fixedPath += '?' + new URLSearchParams(params);
  }

  let jsonBody = '';
  if (input.body) {
    if (typeIn) {
      input.body = typeIn(input.body);
    }
    jsonBody = JSON.stringify(input.body);
  }

  const ctrl = new AbortController();
  const sig = ctrl.signal;

  setTimeout(() => ctrl.abort(), 20000);
  return new Request(fixedPath, {
    body: jsonBody || null,
    method: method,
    signal: sig,
    headers: { 'Content-Type': 'application/json' },
  });
};
